import { Input, Label, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@drs-ops/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RATE_LABELS } from "@/pages/settings/rateLabels";
import type { Rate } from "@/types/rate";
import { formatDistanceToNow } from "./time";

const UNIT_SUFFIX: Record<string, string> = {
  per_hour: "/ hr",
  per_day: "/ day",
  per_mile: "/ mile",
  flat: "USD",
  multiplier: "",
};

const DOLLAR_UNITS = new Set(["per_hour", "per_day", "per_mile", "flat"]);

export interface RatesGroupProps {
  title: string;
  rates: Rate[];
  onChange: (key: string, value: number) => void;
}

export function RatesGroup({ title, rates, onChange }: RatesGroupProps): React.ReactElement {
  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {rates.map((r) => {
            const unit = r.unit ?? "";
            const showDollar = DOLLAR_UNITS.has(unit);
            const suffix = UNIT_SUFFIX[unit] ?? "";
            const label = RATE_LABELS[r.key] ?? r.key.replace(/_/g, " ");
            return (
              <div key={r.key} className="flex flex-col gap-1">
                <Label htmlFor={r.key}>{label}</Label>
                <div className="flex items-center gap-1">
                  {showDollar && <span className="text-sm text-muted-foreground">$</span>}
                  <Input
                    id={r.key}
                    type="number"
                    step="0.01"
                    defaultValue={r.value}
                    onBlur={(e) => {
                      const v = Number(e.currentTarget.value);
                      if (Number.isFinite(v) && v !== r.value) onChange(r.key, v);
                    }}
                  />
                  {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
                </div>
                {r.updated_by_display && r.updated_at ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default text-xs text-muted-foreground">
                        edited by {r.updated_by_display} · {formatDistanceToNow(r.updated_at)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{new Date(r.updated_at).toLocaleString()}</TooltipContent>
                  </Tooltip>
                ) : (
                  <span className="text-xs text-muted-foreground">no prior edits</span>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
